import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlarmClock, X } from 'lucide-react';
import { api } from '../lib/api';

/**
 * The two-hour reply promise, put in front of the person who has to keep it.
 *
 * Pops up on any Purchase page while a Sales enquiry is past its deadline or in
 * the last stretch before it. "Remind me later" hides it for 30 minutes — and
 * then it comes back, for as long as anything is still waiting. A NEW enquiry
 * crossing its deadline brings it back immediately, snooze or not: a snooze
 * was agreed against the list that was on screen, not against ones that had
 * not happened yet.
 *
 * Not shown on the Sales Enquiries page itself — that page is the answer.
 */

type Item = {
  key: string;
  tab: string;
  ref: string;
  what: string;
  tat: { state: string; label: string };
};
type Snapshot = { targetMins: number; open: number; breached: number; dueSoon: number; items: Item[] };

const SNOOZE_KEY = 'proc.enquiryReminder.snooze';
const SNOOZE_MS = 30 * 60_000;

type Snooze = { until: number; seen: string[] };

/** Browser storage can throw (private mode, blocked site data) — treat as no snooze. */
function readSnooze(): Snooze | null {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    return raw ? (JSON.parse(raw) as Snooze) : null;
  } catch {
    return null;
  }
}
function writeSnooze(s: Snooze) {
  try { localStorage.setItem(SNOOZE_KEY, JSON.stringify(s)); } catch { /* per-viewer convenience only */ }
}

export default function EnquiryReminderPopup() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [snooze, setSnooze] = useState<Snooze | null>(() => readSnooze());
  // Re-evaluate the snooze as time passes, without waiting for a refetch.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const { data } = useQuery<Snapshot>({
    queryKey: ['procurement', 'sales-enquiries', 'reminder'],
    queryFn: () => api.get('/api/procurement/sales-enquiries/reminder'),
    refetchInterval: 2 * 60_000,
    staleTime: 60_000,
  });

  const urgent = useMemo(
    () => (data?.items || []).filter((i) => i.tat.state === 'breached' || i.tat.state === 'due_soon'),
    [data],
  );

  const hasNew = !!snooze && urgent.some((i) => !snooze.seen.includes(i.key));
  const snoozed = !!snooze && now < snooze.until && !hasNew;
  const onDesk = pathname.startsWith('/procurement/sales-enquiries');
  const open = urgent.length > 0 && !snoozed && !onDesk;

  const later = () => {
    const s = { until: Date.now() + SNOOZE_MS, seen: urgent.map((i) => i.key) };
    writeSnooze(s);
    setSnooze(s);
  };
  const goReply = () => {
    later();
    navigate('/procurement/sales-enquiries');
  };

  // Escape = remind me later; focus lands on the action that clears it.
  const primary = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    primary.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') later(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !data) return null;

  const hrs = Math.round((data.targetMins || 120) / 60);
  const breached = urgent.filter((i) => i.tat.state === 'breached').length;
  const shown = urgent.slice(0, 6);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={later}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="enq-reminder-title"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ animation: 'enqPop 180ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{'@keyframes enqPop{from{opacity:0;transform:scale(.96) translateY(6px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion: reduce){[role=alertdialog]{animation:none!important}}'}</style>

        <div className="flex items-start gap-3 border-b border-slate-100 bg-red-50 px-5 py-4">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
            <AlarmClock size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="enq-reminder-title" className="text-[15px] font-bold text-slate-900">
              {urgent.length} Sales enquir{urgent.length === 1 ? 'y is' : 'ies are'} waiting on you
            </h2>
            <p className="mt-0.5 text-[12.5px] text-slate-600">
              Sales were promised a reply within {hrs}h.
              {breached > 0 && <> <b className="text-red-700">{breached} past the deadline.</b></>}
            </p>
          </div>
          <button type="button" onClick={later} aria-label="Remind me later" className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-600">
            <X size={16} />
          </button>
        </div>

        <ul className="max-h-[46dvh] divide-y divide-slate-100 overflow-y-auto">
          {shown.map((i) => (
            <li key={i.key} className="flex items-start justify-between gap-3 px-5 py-2.5">
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-slate-800">
                  {i.tab} <span className="font-mono">{i.ref}</span>
                </div>
                {i.what && <div className="truncate text-[11.5px] text-slate-500">{i.what}</div>}
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                i.tat.state === 'breached' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
              }`}>
                {i.tat.label}
              </span>
            </li>
          ))}
          {urgent.length > shown.length && (
            <li className="px-5 py-2 text-[11.5px] text-slate-500">+{urgent.length - shown.length} more</li>
          )}
        </ul>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 px-5 py-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={later} className="rounded-lg px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">
            Remind me in 30 min
          </button>
          <button
            ref={primary}
            type="button"
            onClick={goReply}
            className="rounded-lg bg-teal-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-teal-700"
          >
            Reply now
          </button>
        </div>
      </div>
    </div>
  );
}
